import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Upload, 
  CheckSquare, 
  BookOpen, 
  FileText, 
  BarChart3,
  UserCog,
  LogOut,
  Building2,
  Zap
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { cn } from '../../lib/utils';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/upload', icon: Upload, label: 'Upload data' },
  { path: '/transactions', icon: CheckSquare, label: 'Review Queue' },
  { path: '/journal', icon: BookOpen, label: 'Journal Entries' },
  { path: '/accounts', icon: FileText, label: 'Chart of Accounts' },
  { path: '/reports', icon: BarChart3, label: 'Reporting' },
  { path: '/settings', icon: UserCog, label: 'Account' },
];

export default function MobileNav({ onClose }) {
  const { logout, user } = useAuth();
  const { credits, tier, org } = useOrg();
  const location = useLocation();

  const tierLabel = tier === 'starter' ? 'Starter' : tier === 'pro' ? 'Pro' : tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Free';
  const monthlyAlloc = credits?.monthly_allocation || 0;
  const monthlyUsed = credits?.monthly_used || 0;
  const usagePercent = monthlyAlloc > 0 ? Math.min(100, Math.round((monthlyUsed / monthlyAlloc) * 100)) : 0;
  const remaining = credits?.total_available || 0;

  const handleLogout = () => {
    logout();
    onClose();
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Logo + Company */}
      <div className="px-6 py-4 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#D94E18] rounded-md flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold text-lg text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            kaya.ai
          </span>
        </div>
        {org?.company_name && (
          <p className="mt-2 text-xs font-semibold text-zinc-500 truncate pl-0.5" data-testid="mobile-company-name" title={org.company_name}>
            {org.company_name}
          </p>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    'nav-item',
                    isActive && 'active'
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User info, credit meter & logout */}
      <div className="border-t border-zinc-200 p-3">
        {/* Credit meter */}
        {credits && (
          <div className="mb-3 px-3 py-2.5 bg-zinc-50 rounded-lg" data-testid="mobile-credit-meter">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#D94E18]" />
                <span className="text-xs font-semibold text-zinc-700">Credits</span>
              </div>
              {credits.dev_mode ? (
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">DEV</span>
              ) : (
                <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">{tierLabel}</span>
              )}
            </div>
            <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  usagePercent >= 100 ? "bg-red-500" : usagePercent >= 80 ? "bg-amber-500" : "bg-[#D94E18]"
                )}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-zinc-400">{monthlyUsed} / {monthlyAlloc} used</span>
              <span className="text-[10px] text-zinc-500 font-medium">{remaining} left</span>
            </div>
          </div>
        )}

        <div className="px-4 py-3 bg-zinc-50 rounded-md">
          <p className="text-sm font-semibold text-zinc-900 truncate">{user?.name}</p>
          <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
        </div>

        <button
          onClick={handleLogout}
          data-testid="mobile-logout-btn"
          className="nav-item w-full mt-2 text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}
